import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Sun, Moon, Star, ArrowLeft, ChevronDown, ShieldAlert, Sparkles } from 'lucide-react';
import RelatedTools from '../components/RelatedTools';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FloatingActions from '../components/FloatingActions';
import SEO from '../components/SEO';
import { useLang } from '../context/LanguageContext';
import { PLANETS_CONFIG } from '../data/upayPlanetsConfig';

const ICONS = { Sun, Moon, Star };

// Map config to the lightweight summary shape this master page needs.
const PLANETS = PLANETS_CONFIG.map((p) => ({
  id: p.slug,
  name: p.name,
  nameEn: p.nameEn,
  color: p.color,
  iconBg: p.iconBg,
  iconColor: p.iconColor,
  icon: ICONS[p.icon] || Star,
  short: p.short,
  day: p.day,
  intro: p.intro,
  practical: p.practical,
  mantra: p.mantra,
  mantraNote: p.mantraNote,
  rudraksh: p.rudraksh,
  ratna: p.ratna,
}));

/**
 * UpayPage — आचार्य Vivek Mudgal जी का आधिकारिक नवग्रह उपाय पृष्ठ।
 * SEO + GEO (Generative Engine Optimization) optimised for ChatGPT/Gemini/Perplexity/Claude.
 * EEAT-aligned, human-authored tone, voice-search friendly headings.
 */


const FAQ = [
  {
    q: 'कौन सा ग्रह शांत करने का सबसे आसान उपाय क्या है?',
    a: 'सबसे आसान उपाय है उस ग्रह के संबंधित दिन उसका मन्त्र 108 बार जपना और उसी दिन से जुड़ा दान करना। उदाहरण: सूर्य के लिए रविवार को सूर्य अर्घ्य + सूर्य मन्त्र; शनि के लिए शनिवार को सरसों तेल का दीपक।',
  },
  {
    q: 'क्या रत्न बिना ज्योतिषी की सलाह के पहन सकते हैं?',
    a: 'नहीं। रत्न (माणिक, मोती, मूंगा, पन्ना, पुखराज, हीरा, नीलम, गोमेद, लहसुनिया) कभी भी विद्वान वैदिक ज्योतिषाचार्य की कुंडली-विश्लेषण के बाद ही धारण करें। गलत रत्न से लाभ के बजाय हानि होती है।',
  },
  {
    q: 'महादशा में ग्रह शांति का उपाय कैसे करें?',
    a: 'महादशा वाले ग्रह का: (1) दैनिक मन्त्र जप, (2) सम्बंधित रुद्राक्ष धारण, (3) उस ग्रह के दिन सेवा-दान, (4) ज्योतिषीय परामर्श से उपयुक्त रत्न — चारों मिलकर पूर्ण उपाय बनाते हैं।',
  },
  {
    q: 'क्या ये उपाय वैज्ञानिक रूप से प्रामाणिक हैं?',
    a: 'ये पराशर परम्परा (Brihat Parashara Hora Shastra), फलदीपिका, सारावली जैसे प्रामाणिक ग्रंथों पर आधारित हैं और आचार्य Vivek Mudgal जी के 33+ वर्षों के अनुभव से व्यवहारिक रूप से सिद्ध हैं।',
  },
  {
    q: 'क्या ये उपाय हर व्यक्ति पर एक समान असर करते हैं?',
    a: 'मन्त्र, दान और सेवा वाले उपाय हर किसी के लिए सुरक्षित हैं और लाभदायक हैं। परन्तु रत्न और रुद्राक्ष कुंडली अनुसार ही धारण करें — क्योंकि एक ही ग्रह किसी की कुंडली में शुभ और किसी की में अशुभ हो सकता है।',
  },
];

export default function UpayPage() {
  const { lang } = useLang();
  const [openId, setOpenId] = useState('surya');
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="नवग्रह उपाय | ग्रह शांति, मन्त्र, रुद्राक्ष, रत्न | आचार्य Vivek Mudgal"
        description="सूर्य, चंद्र, मंगल, बुध, गुरु, शुक्र, शनि, राहु, केतु — नवग्रहों के व्यवहारिक उपाय, मन्त्र, रुद्राक्ष और रत्न। आचार्य Vivek Mudgal जी के 33+ वर्षों के अनुभव से प्रामाणिक वैदिक ज्योतिष उपाय।"
        path="/upay"
        keywords="नवग्रह उपाय, ग्रह शांति उपाय, ग्रह दोष उपाय, महादशा उपाय, कमजोर ग्रह उपाय, वैदिक ज्योतिष उपाय, सूर्य उपाय, चंद्र उपाय, मंगल उपाय, बुध उपाय, गुरु उपाय, शुक्र उपाय, शनि उपाय, राहु उपाय, केतु उपाय, मन्त्र, रुद्राक्ष, रत्न, vivek mudgal"
        breadcrumbs={[
          { name: lang === 'hi' ? 'होम' : 'Home', path: '/' },
          { name: lang === 'hi' ? 'उपाय' : 'Remedies', path: '/upay' },
        ]}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: 'नवग्रह उपाय — मैं कौन सा या क्या उपाय करूँ',
          author: { '@type': 'Person', name: 'Acharya Vivek Mudgal', url: 'https://viratastro.cloud/about' },
          publisher: { '@type': 'Organization', name: 'ViratAstro', url: 'https://viratastro.cloud' },
          inLanguage: 'hi-IN',
          about: 'Vedic astrology remedies for nine planets — Sun, Moon, Mars, Mercury, Jupiter, Venus, Saturn, Rahu, Ketu',
          mainEntity: {
            '@type': 'FAQPage',
            mainEntity: FAQ.map(f => ({
              '@type': 'Question',
              name: f.q,
              acceptedAnswer: { '@type': 'Answer', text: f.a },
            })),
          },
        }}
      />
      <Header />

      {/* Hero */}
      <section className="relative pt-32 pb-12 cosmic-bg overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-950/80 via-purple-900/50 to-amber-950/30" />
        <div className="container-custom relative z-10 max-w-4xl">
          <Link to="/" className="inline-flex items-center gap-1.5 text-text-muted hover:text-gold text-sm mb-5" data-testid="back-home-link">
            <ArrowLeft className="w-4 h-4" /> {lang === 'hi' ? 'होम' : 'Home'}
          </Link>
          <span className="inline-block text-[11px] tracking-[0.3em] uppercase text-gold font-semibold mb-3">
            नवग्रह शांति उपाय
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-text-main leading-tight mb-4">
            <span className="text-gold-gradient">मैं कौन सा या क्या उपाय करूँ</span>
          </h1>
          <p className="text-text-main/90 text-lg sm:text-xl mb-2 leading-relaxed">
            <strong className="text-gold">सूर्य, चंद्र, मंगल, बुध, गुरु, शुक्र, शनि, राहु और केतु</strong> — नवग्रहों के <strong>व्यवहारिक उपाय, मन्त्र, रुद्राक्ष और रत्न</strong>।
          </p>
          <p className="text-text-muted text-sm sm:text-base max-w-2xl">
            आचार्य <strong className="text-gold">Vivek Mudgal</strong> जी के 33+ वर्षों के अनुभव और शास्त्रीय परम्परा (Brihat Parashara Hora Shastra, फलदीपिका, सारावली) पर आधारित प्रामाणिक वैदिक ज्योतिष उपाय।
          </p>
          <div className="divine-divider mt-6 max-w-xs" />
        </div>
      </section>

      {/* Quick Featured-snippet style intro */}
      <section className="section-padding pt-8">
        <div className="container-custom max-w-4xl">
          <div className="glass-card p-6 border-gold/25 mb-10" data-testid="upay-intro-card">
            <h2 className="font-heading text-2xl text-gold mb-3">किस ग्रह का उपाय करें — संक्षेप में</h2>
            <p className="text-text-main mb-2">
              यदि आपकी <strong>कुंडली में कोई ग्रह कमज़ोर</strong> या <strong>महादशा / अंतर्दशा</strong> चल रही है, तो उस ग्रह के नीचे दिए गए <strong>मन्त्र, रुद्राक्ष, रत्न और सेवा-दान</strong> मिलकर पूर्ण <strong>ग्रह शांति उपाय</strong> बनाते हैं।
            </p>
            <p className="text-text-muted text-sm">
              <strong>शुरुआत कैसे करें?</strong> सबसे पहले उस ग्रह से जुड़ा <em>दिन</em> चुनें (उदा. शनि → शनिवार), उसका मन्त्र 108 बार जपें, और उस दिन का सरल दान करें। रत्न धारण से पहले विद्वान ज्योतिषी से कुंडली विश्लेषण अवश्य करवाएँ।
            </p>
          </div>

          {/* Planet Cards */}
          <div className="space-y-4" data-testid="planets-list">
            {PLANETS.map((p) => {
              const open = openId === p.id;
              const Icon = p.icon;
              return (
                <article
                  key={p.id}
                  className={`glass-card overflow-hidden border ${open ? 'border-gold/40' : 'border-white/10'} transition-all`}
                  data-testid={`planet-${p.id}`}
                  id={p.id}
                >
                  <button
                    onClick={() => setOpenId(open ? '' : p.id)}
                    className="w-full flex items-center justify-between gap-4 p-5 text-left hover:bg-white/[0.03] transition-colors"
                    data-testid={`planet-${p.id}-toggle`}
                  >
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      <div className={`w-12 h-12 rounded-2xl ${p.iconBg} flex items-center justify-center shrink-0`}>
                        <Icon className={`w-6 h-6 ${p.iconColor}`} />
                      </div>
                      <div className="min-w-0">
                        <h2 className="font-heading text-xl sm:text-2xl text-text-main">
                          {p.name} ग्रह को शांत / प्रसन्न करने के उपाय
                        </h2>
                        <p className="text-text-muted text-xs mt-0.5">
                          {p.nameEn} • {p.short} • <span className="text-gold/80">{p.day}</span>
                        </p>
                      </div>
                    </div>
                    <ChevronDown className={`w-5 h-5 text-gold shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
                  </button>

                  {open && (
                    <div className="px-5 pb-6 pt-1 space-y-4 border-t border-white/5">
                      <p className="text-text-main/90 leading-relaxed pt-3">
                        {p.intro}
                      </p>

                      <div>
                        <h3 className="text-gold font-semibold text-sm tracking-wide uppercase mb-2">
                          व्यवहारिक एवं दैनिक जीवन के उपाय
                        </h3>
                        <ul className="space-y-2 text-text-main/90 text-[15px] leading-relaxed">
                          {p.practical.map((item, i) => (
                            <li key={i} className="flex gap-2">
                              <span className="text-gold mt-1.5 shrink-0">◆</span>
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="grid sm:grid-cols-3 gap-3 pt-2">
                        <div className="bg-purple-950/40 border border-gold/15 rounded-xl p-4">
                          <p className="text-[10px] uppercase tracking-[0.2em] text-gold/70 mb-1">मन्त्र</p>
                          <p className="font-heading text-base text-text-main leading-relaxed">{p.mantra}</p>
                          <p className="text-[11px] text-text-muted/80 italic mt-1.5">{p.mantraNote}</p>
                        </div>
                        <div className="bg-purple-950/40 border border-gold/15 rounded-xl p-4">
                          <p className="text-[10px] uppercase tracking-[0.2em] text-gold/70 mb-1">रुद्राक्ष</p>
                          <p className="text-text-main">{p.rudraksh}</p>
                        </div>
                        <div className="bg-purple-950/40 border border-gold/15 rounded-xl p-4">
                          <p className="text-[10px] uppercase tracking-[0.2em] text-gold/70 mb-1">रत्न</p>
                          <p className="text-text-main">{p.ratna}</p>
                        </div>
                      </div>
                      <div className="pt-2 text-right">
                        <Link
                          to={`/upay/${p.id}`}
                          className="inline-flex items-center gap-1.5 text-sm text-gold font-semibold hover:underline"
                          data-testid={`planet-${p.id}-deep-link`}
                        >
                          {p.name} ग्रह के विस्तृत उपाय पढ़ें →
                        </Link>
                      </div>
                    </div>
                  )}
                </article>
              );
            })}
          </div>

          {/* FAQs */}
          <div className="mt-14" data-testid="upay-faqs">
            <h2 className="font-heading text-3xl text-text-main mb-2">अक्सर पूछे जाने वाले प्रश्न (FAQs)</h2>
            <p className="text-text-muted text-sm mb-6">नवग्रह उपाय से जुड़े सबसे आम सवालों के सीधे जवाब।</p>
            <div className="space-y-3">
              {FAQ.map((f, i) => {
                const open = openFaq === i;
                return (
                  <div key={i} className="glass-card border-white/10" data-testid={`faq-${i}`}>
                    <button
                      onClick={() => setOpenFaq(open ? null : i)}
                      className="w-full flex items-center justify-between gap-3 p-4 text-left"
                    >
                      <span className="text-text-main font-medium">{f.q}</span>
                      <ChevronDown className={`w-4 h-4 text-gold transition-transform ${open ? 'rotate-180' : ''}`} />
                    </button>
                    {open && <p className="px-4 pb-4 text-text-muted text-sm leading-relaxed">{f.a}</p>}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Disclaimer */}
          <div className="mt-12 glass-card p-6 border-red-500/30" data-testid="upay-disclaimer">
            <div className="flex items-center gap-2 mb-3">
              <ShieldAlert className="w-5 h-5 text-red-400" />
              <h2 className="font-heading text-2xl text-text-main">महत्वपूर्ण सूचना</h2>
            </div>
            <p className="text-text-main/90 leading-relaxed italic">
              "रत्न धारण करने से पहले विद्वान् वैदिक ज्योतिषीय ब्राह्मण से अपनी जन्म कुंडली का विश्लेषण अवश्य करवाएँ, क्योंकि गलत रत्न धारण करने से लाभ के स्थान पर हानि होने की संभावना होती है।"
            </p>
            <p className="text-text-muted text-xs mt-3">
              — आचार्य Vivek Mudgal | 33+ वर्षों का पराशरीय अनुभव | फरीदाबाद
            </p>
          </div>

          {/* CTA */}
          <div className="mt-10 text-center">
            <Link
              to="/poocho-guruji"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-gradient-to-r from-gold to-amber-500 text-purple-950 font-bold shadow-xl hover:scale-[1.02] active:scale-[0.98] transition-transform"
              data-testid="cta-poocho-guruji"
            >
              <Sparkles className="w-4 h-4" />
              अपनी कुंडली के लिए specific उपाय जानें — ₹99 में
            </Link>
            <p className="text-text-muted text-xs mt-3">
              पूछो Guruji में अपनी जन्म-तिथि के साथ प्रश्न पूछें — आचार्य Vivek जी आपकी कुंडली के अनुसार सटीक उपाय देंगे।
            </p>
          </div>
        </div>
      </section>

      <RelatedTools />
      <FloatingActions />
      <Footer />
    </div>
  );
}
